import { useQuery } from "@tanstack/react-query";

import { supabase } from "@/shared/lib/supabase";
import { serverEncryption } from "@/shared/utils/serverEncryption";

interface UseDeceasedEINDataResult {
  last4: string | null;
  fullEIN: string | null;
  hasEIN: boolean;
  isLoading: boolean;
}

export function useDeceasedEINData(estateId: number | undefined): UseDeceasedEINDataResult {
  const { data, isLoading } = useQuery({
    queryKey: ["deceasedEINData", estateId],
    queryFn: async () => {
      if (!estateId) return null;

      const { data: estateData, error } = await supabase
        .from("estates")
        .select("ein_encrypted")
        .eq("estate_id", estateId)
        .single();

      if (error || !estateData?.ein_encrypted) return null;

      try {
        const last4 = await serverEncryption.decryptLast4(
          estateData.ein_encrypted,
          "person",
          estateId,
          "ein",
        );
        return { last4, encrypted: estateData.ein_encrypted };
      } catch {
        return null;
      }
    },
    enabled: !!estateId,
    retry: 1,
    staleTime: 5 * 60 * 1000,
  });

  const decryptFullEIN = useQuery({
    queryKey: ["deceasedEINFull", estateId],
    queryFn: async () => {
      if (!estateId || !data?.encrypted) return null;

      try {
        return await serverEncryption.decrypt(
          data.encrypted,
          "person",
          estateId,
          "ein",
        );
      } catch {
        return null;
      }
    },
    enabled: false,
  });

  return {
    last4: data?.last4 || null,
    fullEIN: decryptFullEIN.data || null,
    hasEIN: !!data,
    isLoading,
  };
}
